import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@koob/dates';

export interface FolderStayDestination {
  city: string;
  nights: number;
}

export interface FolderStayCardProps {
  startAt?: string | null;
  endAt?: string | null;
  nights?: number | null;
  destinations: FolderStayDestination[];
}

/**
 * KD-3842 stay of a client folder: dates and nights, the hotel destinations
 * as secondary information. "Not set yet" until a service is attached.
 */
export default function FolderStayCard({
  startAt,
  endAt,
  nights,
  destinations,
}: FolderStayCardProps) {
  const { t } = useTranslation('folders');
  const hasStay = Boolean(startAt && endAt);

  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-xl border border-solid border-gray-200 bg-white px-4 py-3.5">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
        {t('summary.stay')}
      </span>
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-lg font-bold text-gray-900">
          {hasStay
            ? `${formatDate(startAt, 'DD/MM/YYYY')} → ${formatDate(
                endAt,
                'DD/MM/YYYY',
              )}`
            : t('summary.stayNotSet')}
        </span>
        {hasStay && nights !== null && nights !== undefined && (
          <span className="text-sm text-gray-500">
            · {t('summary.nights', { count: nights })}
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 border-0 border-t border-solid border-gray-100 pt-3 text-xs text-gray-600">
        <span className="font-semibold uppercase tracking-wider text-gray-400">
          {t('summary.destinations')}
        </span>
        {destinations.length === 0 ? (
          <span className="text-gray-500">
            {t('summary.destinationsEmpty')}
          </span>
        ) : (
          destinations.map((destination) => (
            <span
              key={destination.city}
              className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5"
            >
              <i className="fa-regular fa-location-dot" aria-hidden="true" />
              {destination.city}
              {destination.nights > 0 && (
                <span className="text-gray-400">
                  {t('summary.nightsShort', { count: destination.nights })}
                </span>
              )}
            </span>
          ))
        )}
      </div>
    </div>
  );
}
